import './wallet-management.css'
import '../../pages/deposit/deposit.css'

import NiceModal, { useModal } from '@ebay/nice-modal-react'
import sha256 from 'crypto-js/sha256'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Portal } from 'react-portal'

import gift from '../../assets/wallet-management/deposit_gift.webp'
import { LOADING_HTML } from '../../constants/casino'
import { PHONE_COUNTRY_CODE } from '../../constants/env'
import useDimensions from '../../hooks/useDimensions'
import useDragScroll from '../../hooks/useDragScroll'
import useVipCenter from '../../hooks/useVipCenter'
import type { User } from '../../store/slices/userSlice'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { PaymentChannel } from '../../types/wallet-management'
import {
  getAdjustAttribution,
  getAdjustWebUUID,
  trackAdjustEvent,
} from '../../utils/adjust'
import { getAdsInfo } from '../../utils/ads-info'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import closeModal from '../../utils/closeModal'
import {
  getAmountBonusMarker,
  getBonusValue,
  hasAnyBonusTier,
  resolvePresetAmounts,
} from '../../utils/deposit-bonus'
import { getDeviceInfo } from '../../utils/device'
import { getDeviceId } from '../../utils/device-id'
import { trackEvent, trackFb, trackTwitter } from '../../utils/gtm'
import { getNativeAttributionMeta } from '../../utils/nativeBridge'
import { numberWithCommas } from '../../utils/number'
import {
  Backdrop,
  BannerPopupModal,
  BrandLoading,
  GlowButton,
  Spinner,
  ToastMessage,
} from '../common'
import GlowRing from '../common/glow-ring'
import type { Item } from '../common/scroll-selector'
import Icon from '../icon'
import AmountSelector from '../payment-form/amount-selector'

// Payment-channel IDs that route through the crypto flow (auto-submitted on
// selection). Kept in one place so the isCrypto check and the unavailable-crypto
// fallback stay in sync.
const CRYPTO_CHANNEL_IDS = [369, 370]

// Ceiling on a height reported by the framed payment page. Anything past this
// is broken or hostile, and the frame keeps its CSS height instead.
const MAX_FRAME_HEIGHT = 20000

type Promo = Item & {
  content: string
  bonus_amount: number
  total_credit_amount: number
  text: string | null
  multiple: string
  linear: string[]
}

// Which pay-limit bound `amount` violates for `channel`, or null when it's
// within range (an empty amount is treated as no violation — it falls back to
// the channel default).
const getPayLimitViolation = (
  channel: PaymentChannel,
  amount: string,
): { bound: 'min' | 'max'; value: number } | null => {
  const value = Number(amount.replaceAll(',', ''))
  if (!value) return null
  const { minimum, maximum } = channel.pay_limit
  if (minimum && value < minimum) return { bound: 'min', value: minimum }
  if (maximum && value > maximum) return { bound: 'max', value: maximum }
  return null
}

// Resolve the amount to use when switching to `channel`. Non-crypto channels
// reset to their default as before. Crypto channels hide the amount editor and
// auto-submit on selection, so preserve whatever amount the user already
// entered — only fall back to the channel default when the current amount is
// empty or outside this channel's pay limits. This amount is not sent to the
// backend for crypto (it has no client-side validation there any more), but
// it still feeds the value reported to trackers on submit.
const resolveAmountForChannel = (
  channel: PaymentChannel,
  currentAmount: string,
): string => {
  const fallback = channel.default_fixed_amount
    ? numberWithCommas(Number(channel.default_fixed_amount))
    : ''
  if (!CRYPTO_CHANNEL_IDS.includes(Number(channel.id))) return fallback

  const value = Number(currentAmount.replaceAll(',', ''))
  if (!value || getPayLimitViolation(channel, currentAmount)) return fallback
  return numberWithCommas(value)
}

// The preset that starts out selected for a channel: it opens on its default
// amount, so the matching chip is highlighted and its bonus applies without the
// player having to click what is already in the field. Crypto channels hide the
// amount editor and auto-submit, and carry no deposit bonus, so nothing is
// selected there.
const defaultSelectedAmount = (
  channel: PaymentChannel | null | undefined,
): number | null =>
  !channel || CRYPTO_CHANNEL_IDS.includes(Number(channel.id))
    ? null
    : Number(channel.default_fixed_amount) || null

const formatPayLimitRange = (
  payLimit: PaymentChannel['pay_limit'],
  { prefix = '', suffix = '' }: { prefix?: string; suffix?: string } = {},
) => {
  const range = [payLimit.minimum, payLimit.maximum]
    .filter(Boolean)
    .map(amount => `${prefix}${numberWithCommas(amount)}`)
    .join('-')
  return `${range}${suffix}`
}

const DepositModal = () => {
  const { isDesktop } = useDimensions()
  // enablePopup:false — this modal only needs hasDeposited to gate the bonus
  // switch below; the FE-04 tier-up popup is handled elsewhere and firing it
  // again from inside the deposit flow would be a duplicate check.
  // refetchOnMount:'always' — nothing invalidates vip_status on a successful
  // deposit, so the default 5-minute staleTime would otherwise let this very
  // deposit's own result stay cached as hasDeposited:false: close the modal
  // and reopen it (a fresh mount) within that window and the switch would
  // still show, letting bonuses be turned off after the first deposit.
  const {
    hasDeposited,
    isFetching: isVipStatusFetching,
    isError: isVipStatusError,
    // Called from handleBack — see the note there. refetchOnMount:'always'
    // only covers a fresh mount; returning from the payment iframe to this
    // same still-mounted form is a different transition it doesn't reach.
    refetchStatus,
  } = useVipCenter({
    enablePopup: false,
    refetchOnMount: 'always',
  })
  // hasDeposited defaults to false while the query is loading, while it fails
  // outright, AND — this is the case isLoading alone would miss — while
  // refetchOnMount:'always' triggers a background refetch that already has a
  // stale cached value to serve (isLoading is false the instant cached data
  // exists, even mid-refetch). isFetching covers all three: it's true on any
  // in-flight fetch, not just a first one with no cache. Without it, a
  // returning depositor reopening the modal shortly after their first
  // deposit would see the switch driven by the STALE pre-deposit
  // hasDeposited:false while the fresh (true) value is still in flight — the
  // exact race refetchOnMount:'always' was added to close, above. A failed
  // fetch never self-corrects either (vip_status throws rather than
  // resolving a fallback), so isError still guards that permanently.
  const depositStatusConfirmed = !isVipStatusFetching && !isVipStatusError
  const modal = useModal()
  const {
    t,
    i18n: { language },
  } = useTranslation('walletManagement')
  const channels = useBoundStore(state => state.payment.paymentChannels)
  const email = useBoundStore(state => state.user.email)
  const phone = useBoundStore(state => state.user.phone)
  const setUser = useBoundStore(state => state.user.setUser)
  const bannerPopupModal = useModal(BannerPopupModal)
  const toastMessage = useModal(ToastMessage)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const controller = useRef<AbortController | null>(null)
  const {
    scrollRef: paymentScrollRef,
    canScrollLeft,
    canScrollRight,
    hasDragged,
    onMouseDown,
    onMouseMove,
    onMouseUp,
  } = useDragScroll([channels])

  const [selectedPaymentChannel, setSelectedPaymentChannel] =
    useState<PaymentChannel | null>(channels[0] ?? null)
  const [amount, setAmount] = useState<string>(
    channels[0]?.default_fixed_amount
      ? numberWithCommas(Number(channels[0].default_fixed_amount))
      : '',
  )
  const [selectedPromo, setSelectedPromo] = useState<Promo | null>(null)
  const [promoList, setPromoList] = useState<Promo[]>([])
  const [promoLoading, setPromoLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  // Content height reported by the framed payment page, or null while unknown —
  // then the frame keeps the viewport-derived height from handleIframeStyle.
  const [frameHeight, setFrameHeight] = useState<number | null>(null)
  // Keeps the bonus section in the DOM through its fade/collapse-out so it
  // doesn't vanish abruptly when a fetch returns no promos. `showBonusSection`
  // drives the visible classes; this mirrors it with a delayed unmount.
  const [bonusSectionMounted, setBonusSectionMounted] = useState(false)
  // The skeleton is only shown on the very first load. After a fetch has
  // resolved once, re-fetches (on every amount/channel change) keep the current
  // content (stale-while-revalidate) until the new result arrives, so the loader
  // never flashes again and the section never briefly collapses mid-load when an
  // empty selection changes to one with bonuses.
  const [hasResolvedPromos, setHasResolvedPromos] = useState(false)
  // Where the switch is pointing. What it *means* is derived below, so a
  // channel without amount bonuses needs no correcting effect — one that set
  // this to false after mount cost an extra render, and since the flag is a dep
  // of the promotions fetch, that render fired a second request on every open.
  const [bonusesOn, setBonusesOn] = useState(true)
  // The preset chip the player picked, or null. Tracked separately from the
  // amount field because on a channel with tiers picking a promotion clears the
  // chip's selection while leaving its value in the field — and picking a chip
  // clears the promotion. That trade only exists where the chips carry a bonus
  // of their own, so it is gated on `channelHasBonusTiers` at both call sites.
  const [selectedAmount, setSelectedAmount] = useState<number | null>(
    defaultSelectedAmount(channels[0]),
  )

  const amountValue: string = useMemo(
    () => amount.replaceAll(',', ''),
    [amount],
  )

  const isCrypto = useMemo(() => {
    return CRYPTO_CHANNEL_IDS.includes(Number(selectedPaymentChannel?.id))
  }, [selectedPaymentChannel])

  // Whether the channel list offers any crypto option — gates the
  // "no bonus on crypto" note so it only shows when crypto is actually offered.
  const hasCryptoChannel = useMemo(
    () => channels.some(c => CRYPTO_CHANNEL_IDS.includes(Number(c.id))),
    [channels],
  )

  // The pay-limit bound the entered amount violates (or null when in range) —
  // drives both the inline field error and the submit button's disabled state.
  const amountViolation = useMemo(
    () =>
      selectedPaymentChannel
        ? getPayLimitViolation(selectedPaymentChannel, amount)
        : null,
    [selectedPaymentChannel, amount],
  )

  // The switch is set against one channel's tiers, so it does not outlive that
  // channel: every channel opens with bonuses on, whichever way the switch was
  // left on the last one. Keyed on the channel rather than set at each call
  // site, because the channel changes from three of them — the tile, the
  // auto-select on load, and the back button out of the payment form.
  useEffect(() => {
    setBonusesOn(true)
  }, [selectedPaymentChannel?.id])

  // hasDeposited starts false while the VIP status query is still in flight,
  // so a player can flip the switch off before it resolves. Once it resolves
  // true the switch is unmounted (below), and without this bonusesOn would be
  // stuck false with no control left to turn it back on — silently
  // suppressing both the amount tiers and the promotions list.
  useEffect(() => {
    if (hasDeposited) {
      setBonusesOn(true)
    }
  }, [hasDeposited])

  // Auto-select first channel when channels load
  useEffect(() => {
    if (channels.length && !selectedPaymentChannel) {
      const channel = channels[0]
      setSelectedPaymentChannel(channel)
      setSelectedAmount(defaultSelectedAmount(channel))
      if (channel.default_fixed_amount) {
        setAmount(numberWithCommas(Number(channel.default_fixed_amount)))
      }
    }
  }, [channels])

  // Slide the selected payment channel to the horizontal centre of its
  // scroller, so switching channels animates into view instead of jumping —
  // and so the selected tile's 1.2x scale isn't clipped at either edge.
  // Also re-runs on `hasSubmitted` (not just a dep of the effect's own
  // logic): the picker unmounts while a non-crypto submission shows the
  // iframe, so on `handleBack` it remounts a fresh, unscrolled scroller —
  // and since Back usually restores the very channel that was already
  // selected, `selectedPaymentChannel` keeps the same reference and would
  // otherwise never re-trigger this effect to re-center it.
  useEffect(() => {
    const el = paymentScrollRef.current
    if (!el || !selectedPaymentChannel) return
    const index = channels.findIndex(c => c.id === selectedPaymentChannel.id)
    if (index < 0) return
    const target = el.children[index] as HTMLElement | undefined
    if (!target) return
    const targetRect = target.getBoundingClientRect()
    const containerRect = el.getBoundingClientRect()
    const delta =
      targetRect.left +
      targetRect.width / 2 -
      (containerRect.left + containerRect.width / 2)
    // Guard scrollTo — jsdom (unit tests) doesn't implement it.
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ left: el.scrollLeft + delta, behavior: 'smooth' })
    }
  }, [selectedPaymentChannel, channels, hasSubmitted])

  useEffect(() => {
    if (isDesktop && hasSubmitted && iframeRef.current) {
      handleIframeStyle('inactive')
      setHasSubmitted(false)
    }
  }, [selectedPaymentChannel])

  useEffect(() => {
    if (controller.current) {
      controller.current.abort()
      controller.current = null
    }
    // Not before there is a channel to ask about. The modal's first render
    // happens before the channel list resolves, and that fired a request
    // carrying no channel_id and an empty amount — a second, useless call on
    // every open, ahead of the real one.
    if (!selectedPaymentChannel) return
    controller.current = new AbortController()

    const fetchPromoList = async (signal: AbortSignal) => {
      setPromoLoading(true)
      try {
        const response = await api.post<
          ApiResponse<Array<Omit<Promo, 'label'> & { title: string }>>
        >(
          '/payment.php',
          {
            type: 'autopromotion_list',
            amount: amountValue,
            channel_id: selectedPaymentChannel?.id,
          },
          {
            signal,
          },
        )
        const { status, info } = response.data

        if (status === 1) {
          const formatInfo = info.map(item => ({
            label: item.title,
            ...item,
          }))
          if (selectedPromo) {
            const samePromo = formatInfo.find(
              promo => selectedPromo.id === promo.id && !promo.disabled,
            )
            // Read through the ref, so a response landing after the switch
            // moved cannot reinstate a promotion the player has turned away
            // from — neither to count now nor to wake up on switching back.
            setSelectedPromo(
              promosDimmedRef.current ? null : (samePromo ?? null),
            )
          } else {
            setSelectedPromo(null)
            const firstDepositBonus = formatInfo.find(
              promo => [234, 235].includes(+promo.id) && !promo.disabled,
            )
            if (firstDepositBonus) {
              bannerPopupModal.show({
                imgUrl: `https://www.u2d8899.com/mexicoimages/banner/100_bonus_${language}.webp`,
              })
            }
          }
          setPromoList(formatInfo)
          setHasResolvedPromos(true)
        } else {
          setSelectedPromo(null)
          setPromoList([])
          setHasResolvedPromos(true)
        }
      } catch (error) {
        // An aborted request (each keystroke aborts the previous one) was
        // superseded — leave the loading state to the newer fetch. A real
        // failure clears the skeletons and falls back to "no promos".
        if (signal.aborted) return
        console.error(error)
        setSelectedPromo(null)
        setPromoList([])
        setHasResolvedPromos(true)
      }
      setPromoLoading(false)
    }
    fetchPromoList(controller.current.signal)

    return () => {
      if (controller.current) {
        controller.current.abort()
        controller.current = null
      }
    }
  }, [amountValue, selectedPaymentChannel])

  // The skeleton shows only during the FIRST load. Afterwards re-fetches keep
  // the current content (stale) until the new result lands.
  const showBonusSkeleton = promoLoading && !hasResolvedPromos
  // The section is visible while the first-load skeleton runs or whenever there
  // are promos to pick; it collapses out only on a settled empty result. The
  // switch stays reachable throughout — it hides the list, not the heading.
  // Whether this channel offers amount bonuses at all. The switch only makes
  // sense when there are two sources to choose between.
  const channelHasBonusTiers = hasAnyBonusTier(
    selectedPaymentChannel?.deposit_bonus_tiers,
  )

  // The section is visible while the first-load skeleton runs, whenever there
  // are promos to pick, and whenever the channel has amount bonuses — that last
  // case matters because the switch lives in this section and is the only way
  // to reach those bonuses. Gating it on the promotions list alone stranded a
  // channel whose tiers were real but whose list came back empty. The list
  // inside follows the switch separately.
  const showBonusSection =
    showBonusSkeleton || promoList.length > 0 || channelHasBonusTiers

  // Only a channel that actually has amount bonuses can show them, and only
  // such a channel offers the switch — so on a channel without them the
  // promotions list stays live and ungreyed, exactly as it was before the
  // switch existed.
  const showAmountBonuses = channelHasBonusTiers && bonusesOn && !hasDeposited
  const promosDimmed = channelHasBonusTiers && !bonusesOn

  // Read by the promotions fetch when it completes, which can be long after the
  // render that started it — a captured copy would describe the switch as it
  // was, not as it is.
  const promosDimmedRef = useRef(promosDimmed)
  promosDimmedRef.current = promosDimmed

  // The promotion actually in force. Derived rather than kept in sync: the
  // promotions fetch completes asynchronously, so a response landing after the
  // switch moved could otherwise put one back where it would count. A stored
  // promotion is simply inert here whenever the switch is off.
  const activePromo = promosDimmed ? null : selectedPromo

  // Promotion tiles size to how many there are: one fills the row, two take a
  // half each, three a third — no dead space beside a short list. Past three
  // they keep a third each (minus the two 0.75rem gaps) and the row scrolls.
  const promoTileWidth =
    promoList.length > 3
      ? 'min-w-[calc((100%-1.5rem)/3)] max-w-[calc((100%-1.5rem)/3)]'
      : 'flex-1 min-w-0'

  // The bonus the entered amount earns. Read off the amount itself, bracketed
  // to the highest tier at or below it (see findBonusTier), so a typed 499 earns
  // the 300 tier just as the chip would — and so the tier can never be stale for
  // the amount being deposited, which is what pinning this to the picked chip
  // was working around.
  //
  // A selected promotion outranks it: the two are alternatives, and picking one
  // is choosing it over the other.
  const enteredBonusMarker =
    !showAmountBonuses || activePromo || !selectedPaymentChannel
      ? null
      : getAmountBonusMarker(
          selectedPaymentChannel.deposit_bonus_tiers,
          Number(amountValue),
        )

  // The bonus the deposit will earn, in currency. The amount's own tier and a
  // promo are alternatives, so whichever is in play supplies the one figure that
  // both the BONO line and the total below are built from. Priced off the same
  // figure the marker was resolved from, so a percentage is always a share of
  // the deposit it belongs to.
  const bonusValue = enteredBonusMarker
    ? getBonusValue(enteredBonusMarker, Number(amountValue))
    : Number(activePromo?.bonus_amount ?? 0)
  const totalToReceive = Number(amountValue) + bonusValue

  // Turning bonuses off drops whichever source was chosen. Without this a
  // promotion keeps feeding the bonus total and rides along in `autopromo`
  // while its list is inert — the same trap the crypto branch of the submit
  // body already guards against.
  useEffect(() => {
    if (promosDimmed) {
      setSelectedPromo(null)
      setSelectedAmount(null)
    }
  }, [promosDimmed])

  useEffect(() => {
    if (showBonusSection) {
      setBonusSectionMounted(true)
      return
    }
    // Delay the unmount by the fade/collapse duration so the transition plays.
    const timer = setTimeout(() => setBonusSectionMounted(false), 300)
    return () => clearTimeout(timer)
  }, [showBonusSection])

  useEffect(() => {
    const messageHandler = (message: MessageEvent<string>) => {
      try {
        const data = JSON.parse(message.data) as Record<
          'type' | 'height',
          unknown
        >
        if (data?.type === 'back' && iframeRef.current) {
          handleIframeStyle('inactive')
          setHasSubmitted(false)
        }
        // The payment page reports its own content height, which is the only
        // way to know it: the frame's height is set here, so measuring it back
        // would just return what we already set, and once the page is on the
        // provider's origin its layout cannot be read at all. Following the
        // report is what stops a short page leaving empty frame below it.
        if (data?.type === 'resize') {
          const height = Number(data.height)
          // Ignore junk and absurd values; a bad number would stretch the modal
          // past any usable size.
          if (
            Number.isFinite(height) &&
            height > 0 &&
            height <= MAX_FRAME_HEIGHT
          )
            setFrameHeight(height)
        }
      } catch (error) {
        // do nothing
      }
    }

    window.addEventListener('message', messageHandler)
    return () => {
      window.removeEventListener('message', messageHandler)
    }
  }, [])

  useEffect(() => {
    if (isCrypto) onSubmit(null)
  }, [isCrypto])

  const onSubmit = async (tab: Window | null) => {
    if (!selectedPaymentChannel) return

    setIsLoading(true)
    try {
      const nativeMeta = await getNativeAttributionMeta()
      const deviceId = await getDeviceId()
      const [webUuid, adjustMeta] = await Promise.all([
        getAdjustWebUUID(),
        getAdjustAttribution(),
      ])
      const reqBody = {
        type: 'submitpay',
        // Crypto has no amount editor of its own and no client-side amount
        // validation (below), so it isn't sent as part of the payload —
        // standard channels still confirm an amount in the UI and submit it.
        ...(isCrypto ? {} : { amount: amountValue }),
        pay_type: 1,
        line_type: selectedPaymentChannel.line_type,
        // Deposit bonuses are not allowed on crypto — never let one ride along
        // with a crypto submit even if a promo lingers in state.
        // An amount bonus carries its own promotion id; the promo list's
        // selection is the alternative. They are mutually exclusive by
        // construction, so the tier wins whenever one is in play.
        autopromo: isCrypto
          ? 0
          : (enteredBonusMarker?.bonusId ?? activePromo?.id ?? 0),
        // Which of the two `autopromo` came from, since the id alone does not
        // say: a tier's `bonus_id` and a promotion's `id` are drawn from
        // different sets, so the backend cannot tell them apart. 1 is the
        // amount's own `deposit_bonus_tiers` entry; 0 is a promotion off the
        // list, or no bonus at all. Carries the same crypto guard as
        // `autopromo` — a marker cannot outlive a switch to crypto and claim
        // a tier for a deposit that earns none.
        is_bonus_tier: !isCrypto && enteredBonusMarker ? 1 : 0,
        pay_id: selectedPaymentChannel.id,
        email: email,
        theme: document.body.className,
        meta: {
          device_id: webUuid || deviceId,
          web_uuid: webUuid,
          ...adjustMeta,
          ...getDeviceInfo(),
          ...getAdsInfo(),
          ...(nativeMeta ?? {}),
        },
      }

      const response = await api.post<
        ApiResponse<{
          form: string
          message?: string
          data: { billno: string }
        }>
      >('/payment.php', reqBody)

      const { status, info } = response.data

      if (status === 1 || status === 2) {
        trackEvent('begin_checkout', {
          email,
          transaction_id: info.data.billno,
          value: +amountValue,
          currency: 'MXN',
          items: [
            {
              item_id: selectedPaymentChannel.id,
              item_name: selectedPaymentChannel.show_name,
              price: +amountValue,
              quantity: '1',
              currency: 'MXN',
            },
          ],
        })
        const revenue = Number(amountValue)
        trackAdjustEvent(
          'begin_checkout',
          Number.isFinite(revenue) ? { revenue, currency: 'MXN' } : {},
        )

        // FB
        trackFb('track', 'InitiateCheckout', {
          value: Number(amountValue),
          currency: 'MXN',
        })
        // Twitter — hash PII before sending to a third party
        trackTwitter('event', 'tw-olsug-om2dy', {
          value: amountValue,
          currency: 'MXN',
          contents: [
            {
              transaction_id: info.data.billno,
              platform_id: selectedPaymentChannel.id,
              platform: selectedPaymentChannel.show_name,
              promotion: selectedPromo?.label ?? null,
              promotion_id: selectedPromo?.id ?? null,
            },
          ],
          email_address: sha256(email).toString(),
          phone_number: sha256(`${PHONE_COUNTRY_CODE}${phone}`).toString(),
        })
        if (selectedPaymentChannel.need_bank_card === 1) {
          setUser({
            pending_bind: 0,
            pending_amount: amountValue.toString(),
          } as User)
        }

        if (tab) {
          tab.document.body.innerHTML = info.form
          const form = tab.document.getElementById(
            'pay_form',
          ) as HTMLFormElement
          if (form) {
            form.submit()
          }
          setIsLoading(false)
        } else if (iframeRef.current) {
          handleIframeStyle('active')
          iframeRef.current.srcdoc = info.form
          const newForm = iframeRef.current.contentDocument?.getElementById(
            'pay_form',
          ) as HTMLFormElement
          newForm?.submit()
          setHasSubmitted(true)
        }
      } else {
        // Backend rejected the deposit (e.g. crypto channel unavailable).
        toastMessage.show({
          text:
            typeof info === 'string'
              ? info
              : t('something-went-wrong', {
                  ns: 'common',
                  defaultValue:
                    'Lo siento, algo salió mal. Póngase en contacto con el servicio de atención al cliente.',
                }),
          isValid: false,
        })
        setIsLoading(false)
        if (isCrypto) recoverFromUnavailableCrypto()
      }
    } catch (error) {
      // Request failed outright (provider down / network) — for crypto this
      // would otherwise hang on the loader with no way back, so recover.
      toastMessage.show({
        text: t('something-went-wrong', {
          ns: 'common',
          defaultValue:
            'Lo siento, algo salió mal. Póngase en contacto con el servicio de atención al cliente.',
        }),
        isValid: false,
      })
      setIsLoading(false)
      if (isCrypto) recoverFromUnavailableCrypto()
    }
  }

  const handleIframeStyle = (status: 'active' | 'inactive') => {
    if (iframeRef?.current)
      if (status === 'active') {
        // h- is the floor for a page that never reports its height; max-h- is
        // the ceiling for one taller than the screen, so it scrolls inside the
        // frame instead of pushing the modal past the viewport. A reported
        // height lands as an inline style and wins over the h- class between
        // the two. Without a height here the frame falls back to the browser's
        // ~150px default, since an iframe cannot size itself to its content.
        iframeRef.current.className =
          'static pointer-events-auto w-full h-[calc(100dvh_-_60px)] max-h-[calc(100dvh_-_60px)]'
      } else {
        iframeRef.current.className =
          'pointer-events-none absolute w-0 h-0 border-[none] ring-0'
        iframeRef.current.srcdoc = ''
        // The next payment page is a different document; its height is unknown
        // until it reports, and the previous page's height does not describe it.
        setFrameHeight(null)
      }
  }

  const handleBack = () => {
    // The player may have just completed a real deposit inside the payment
    // iframe — this component never observes that directly (onSubmit's
    // status 1/2 only means the iframe was shown; there is no later
    // "deposit succeeded" message), so hasDeposited could now be stale.
    // Nothing else re-checks it within this same mount (refetchOnMount only
    // covers a fresh mount), so without this a stale hasDeposited:false would
    // let the switch — and a disabled-bonus second deposit — reappear.
    refetchStatus()
    // Deliberately NOT reset here: back usually lands on the very channel the
    // player left, where forcing it back on would silently re-attach a bonus
    // they'd explicitly turned off before submitting. A genuinely different
    // channel (channels[0] no longer matching) still gets a fresh default via
    // the channel-keyed effect above, the same way a tile click would.
    // Landing on a *different* channel drops the promotion the same way the
    // tile does — it belongs to the channel it was picked against. Guarded on
    // the id changing because back usually returns to the very channel the
    // player left, where clearing would throw away their own choice.
    if (channels[0] && channels[0].id !== selectedPaymentChannel?.id) {
      setSelectedPromo(null)
    }
    setSelectedPaymentChannel(channels[0])
    handleIframeStyle('inactive')
    setHasSubmitted(false)
  }

  // Crypto auto-submits on selection and hides the method picker, so an
  // unavailable crypto channel would otherwise strand the user on a blank
  // modal. Switch back to the first non-crypto channel so the standard deposit
  // form reappears. If the list is all-crypto (nothing to fall back to), close
  // the modal — the error toast already explains why, and that beats leaving a
  // blank/stuck screen.
  const recoverFromUnavailableCrypto = () => {
    const fallbackChannel = channels.find(
      channel => !CRYPTO_CHANNEL_IDS.includes(Number(channel.id)),
    )
    if (!fallbackChannel) {
      closeModal(modal)
      return
    }
    setSelectedPaymentChannel(fallbackChannel)
    setAmount(
      fallbackChannel.default_fixed_amount
        ? numberWithCommas(Number(fallbackChannel.default_fixed_amount))
        : '',
    )
  }

  return (
    <Portal>
      {isDesktop && (
        <Backdrop
          zIndex={203}
          isVisible={modal.visible}
          onClick={() => closeModal(modal)}
          aria-label="Close deposit modal"
        />
      )}
      <div
        id="deposit-modal"
        className={classNames(
          'deposit-modal-container hide-scrollbar w-full h-dvh max-w-[600px] dark:bg-primary-bg dark:bg-size-primary-bg z-[204] fixed p-0 rounded-none border-0 left-0 top-0',
          'xl:-translate-x-2/4 xl:-translate-y-2/4 xl:h-[unset] xl:overflow-y-auto xl:rounded-xl xl:left-2/4 xl:top-2/4',
          // A full-height navy panel behind the payment form reads as "nothing
          // loaded". Splitting it — navy over the top half, white below — looks
          // deliberate instead. It's a gradient (background-image), so the dark
          // theme's own `dark:bg-primary-bg` image still overrides it.
          hasSubmitted
            ? 'bg-[linear-gradient(to_bottom,#00225F_0%,#00225F_60px,#ffffff_60px,#ffffff_100%)]'
            : 'bg-primary',
        )}>
        <div className="flex flex-col xl:rounded-t-xl h-10 lg:h-[50px]">
          <div className="flex items-center justify-center relative h-10 xl:h-[50px]">
            {hasSubmitted && !isCrypto && (
              <button
                type="button"
                className="absolute left-0 top-0 flx-ctr px-4 h-full xl:rounded-tl-[10px] dark:left-2 dark:top-1/2 dark:-translate-y-1/2 dark:h-8 dark:w-8 dark:px-0 dark:rounded-[10px] dark:border dark:border-solid dark:border-[rgba(245,197,66,0.35)] dark:bg-[#141414]"
                onClick={handleBack}
                aria-label="Back to form">
                <Icon
                  className="fill-white dark:fill-gold-txt w-4 h-4"
                  id="chevron-left"
                  width={13}
                  height={13}
                />
              </button>
            )}
            <span
              className={classNames(
                'font-bold uppercase text-base xl:text-lg dark:tracking-[0.04em]',
                // Metallic gold in dark: the gradient is painted through the
                // glyphs via background-clip, so the glow has to be a
                // drop-shadow — a text-shadow paints behind the text and would
                // bleed through the transparent fill.
                'dark:bg-[linear-gradient(180deg,#fdf0c0_0%,#f5c542_42%,#dfa93a_62%,#fff3cc_78%,#b8801f_100%)] dark:[-webkit-background-clip:text] dark:bg-clip-text dark:[-webkit-text-fill-color:transparent] dark:text-transparent dark:drop-shadow-[0_0_10px_rgba(245,197,66,0.22)]',
                hasSubmitted ? 'text-white' : 'text-txt',
              )}>
              {t('deposit')}
            </span>
            <button
              className="absolute right-0 top-0 flx-ctr px-4 h-full xl:rounded-tr-[10px]"
              onClick={() => closeModal(modal)}
              aria-label="Close modal">
              <Icon
                className="fill-highlight-gold stroke-highlight-gold [stroke-width:1.5] [stroke-linejoin:round] w-3.5 h-3.5"
                id="close"
                width={12}
                height={12}
              />
            </button>
          </div>
          {!hasSubmitted && (
            <>
              {/* Gold shimmer line */}
              <div
                className="hidden dark:block h-[3px] w-full"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 5%, #774605 20%, #f5c542 40%, #fff8e0 50%, #f5c542 60%, #774605 80%, transparent 95%)',
                }}
              />
              <div className="hidden dark:block h-px w-full bg-[rgba(245,197,66,0.15)] shadow-[0_4px_12px_rgba(245,197,66,0.10)]" />
            </>
          )}
        </div>
        <div className="flex-1 w-full max-h-[calc(100dvh_-_40px)] lg:h-auto lg:max-h-[calc(100dvh_-_70px)] overflow-y-auto hide-scrollbar rounded-xl">
          {channels.length > 0 && (
            <>
              {((hasSubmitted && isLoading) || (isCrypto && isLoading)) && (
                <Portal>
                  <BrandLoading fullScreen overlay />
                </Portal>
              )}

              {
                /* Payment Methods */
                (!hasSubmitted || isCrypto) && (
                  <div className="px-3 xl:px-6">
                    <div className="deposit-panel rounded-lg bg-secondary p-3 mb-3 xl:p-4 xl:mb-4 dark:rounded-xl dark:border dark:border-solid dark:border-[rgba(255,255,255,0.03)] overflow-hidden">
                      <p className="deposit-section-title font-semibold uppercase text-sm text-txt inline-block mb-2 xl:text-base xl:mb-3 dark:text-[#f6d56c] dark:font-bold relative">
                        {t('payment-methods')}
                      </p>
                      <div className="relative">
                        {canScrollLeft && (
                          <div className="absolute pb-3 h-full w-[70px] pointer-events-none transition-all duration-[0.2s] ease-[ease-in-out] -left-8 top-0 flx-ctr pl-0 pr-[30px] animate-arrowAnim z-[1]">
                            <Icon
                              id="chevron-left"
                              width="7"
                              height="14"
                              className="fill-highlight-gold stroke-highlight-gold [stroke-width:1] [stroke-linejoin:round] [stroke-linecap:round]"
                            />
                          </div>
                        )}
                        {canScrollRight && (
                          <div className="absolute pb-3 h-full w-[70px] pointer-events-none transition-all duration-[0.2s] ease-[ease-in-out] -right-7 top-0 flx-ctr pl-[30px] animate-arrowAnim z-[1]">
                            <Icon
                              id="chevron-right"
                              width="7"
                              height="14"
                              className="fill-highlight-gold stroke-highlight-gold [stroke-width:1] [stroke-linejoin:round] [stroke-linecap:round]"
                            />
                          </div>
                        )}
                        <div
                          ref={paymentScrollRef}
                          className="payment-methods-container flex gap-4 overflow-x-auto pt-5 pb-5 xl:gap-6 cursor-grab select-none"
                          onMouseDown={onMouseDown}
                          onMouseMove={onMouseMove}
                          onMouseUp={onMouseUp}
                          onMouseLeave={onMouseUp}>
                          {channels.map(channel => (
                            <button
                              key={channel.id}
                              onClick={() => {
                                if (hasDragged.current) return
                                // A different channel has different tiers, so a
                                // chip picked against the old one's can't carry
                                // over — it starts on the new default instead.
                                setSelectedAmount(
                                  defaultSelectedAmount(channel),
                                )
                                // Same for the promotion: the list is fetched
                                // per channel_id, so one picked against the old
                                // channel is not on offer here — and on crypto
                                // no bonus is on offer at all. Dropped up front
                                // rather than left to the re-fetch, because a
                                // submit landing inside that in-flight window
                                // sent the old channel's promotion as
                                // `autopromo`. Synchronous, not in the
                                // channel-keyed effect: crypto auto-submits in
                                // the same flush, and the deposit-tracking
                                // payload reads the promotion off this render.
                                //
                                // Guarded on the channel actually changing:
                                // clicking the tile already selected changes
                                // nothing the promotions fetch is keyed on, so
                                // no re-fetch would ever put the selection
                                // back — it would just take the player's
                                // promotion away. Crypto clears either way,
                                // since no bonus applies there at all.
                                if (
                                  channel.id !== selectedPaymentChannel?.id ||
                                  CRYPTO_CHANNEL_IDS.includes(
                                    Number(channel.id),
                                  )
                                ) {
                                  setSelectedPromo(null)
                                }
                                setSelectedPaymentChannel(channel)
                                setAmount(prev =>
                                  resolveAmountForChannel(channel, prev),
                                )
                              }}
                              className={classNames(
                                'relative rounded-lg border-2 border-solid px-1 py-2 flex flex-col items-center gap-1 transition-all shrink-0 min-w-[32%] xl:min-w-[28%] xl:py-3 xl:gap-2 dark:bg-[#1a1a1a] dark:border dark:border-solid dark:border-[rgba(255,255,255,0.08)] dark:rounded-lg',
                                selectedPaymentChannel?.id === channel.id
                                  ? // The horizontal margin reserves the
                                    // space the 1.2x scale spills into, so
                                    // the neighbouring tiles aren't
                                    // overlapped.
                                    'scale-[1.2] z-10 mx-[3.2%] xl:mx-[2.8%] shadow-[0_8px_18px_rgba(0,0,0,0.3)] border-highlight deposit-payment-active dark:!border-[#f5c542] dark:text-[#f6e0a0]'
                                  : 'shadow-[0_2px_6px_rgba(0,0,0,0.15)] border-secondary-txt-20 bg-secondary',
                              )}>
                              {channel.is_recommended && (
                                <span className="absolute -top-2 right-0 green-metallic text-white text-[7px] xl:text-[9px] font-bold px-1 py-0.5 rounded-t leading-none whitespace-nowrap">
                                  {channel.is_recommended}
                                </span>
                              )}
                              <img
                                className="max-w-[70px] xl:max-w-[100px] object-contain"
                                src={channel.img}
                                alt={channel.show_name}
                              />
                              <span className="text-xs xl:text-sm text-secondary-txt text-center leading-tight">
                                {channel?.amount_upper_display ??
                                  formatPayLimitRange(channel.pay_limit, {
                                    prefix: '$',
                                  })}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }
              {!hasSubmitted && !isCrypto && (
                <>
                  {/* Amount Section */}
                  {selectedPaymentChannel && (
                    <div className="flex-1 w-full">
                      <div className="px-3 xl:px-6">
                        <div className="deposit-panel rounded-lg bg-secondary p-3 xl:p-4 dark:rounded-xl dark:border dark:border-solid dark:border-[rgba(255,255,255,0.03)] overflow-hidden">
                          <p className="text-sm text-secondary-txt mb-2 xl:mb-3 xl:text-base">
                            {selectedPaymentChannel?.amount_lower_display ??
                              formatPayLimitRange(
                                selectedPaymentChannel.pay_limit,
                                { suffix: ' MXN' },
                              )}
                          </p>
                          <div className="flex rounded-md mb-2 px-3 py-2 border-secondary-txt-20 border bg-primary focus-within:border-highlight transition-colors xl:py-3 dark:rounded-lg dark:border-solid dark:border-[rgba(245,197,66,0.22)] dark:bg-[#0f1012] dark:text-[#fff5d3] dark:focus-within:border-[#f5c542] dark:focus-within:shadow-[0_0_12px_rgba(245,197,66,0.12)]">
                            <input
                              className="flex-1 min-w-0 text-base text-txt bg-transparent"
                              inputMode="numeric"
                              value={amount}
                              onChange={e => {
                                const withoutComma = e.target.value.replaceAll(
                                  ',',
                                  '',
                                )
                                if (/^\d*$/.test(withoutComma)) {
                                  // Typing leaves whichever chip was picked behind: the tier is
                                  // chosen for a specific amount, so it must not survive the
                                  // amount changing under it.
                                  setSelectedAmount(null)
                                  setAmount(numberWithCommas(withoutComma))
                                }
                              }}
                              placeholder="Ingresar cantidad aquí"
                              onFocus={e => {
                                setTimeout(() => {
                                  e.target.selectionStart =
                                    e.target.selectionEnd = 10000
                                }, 0)
                              }}
                              onBlur={() => {
                                const min =
                                  selectedPaymentChannel?.pay_limit.minimum
                                if (min && +amountValue < +min) {
                                  setAmount(numberWithCommas(Number(min)))
                                }
                              }}
                            />
                            {/* What the entered amount earns, in the same terms
                                the chip advertises: a currency figure for a flat
                                preset, a percentage for a percent one. Not
                                converted to money, since the deposit bonus is
                                the backend's to compute. */}
                            {enteredBonusMarker && (
                              <span className="shrink-0 self-center pl-2 text-sm text-highlight-gold xl:text-base">
                                {t('bonus')} +{' '}
                                {enteredBonusMarker.kind === 'flat'
                                  ? `$${numberWithCommas(
                                      enteredBonusMarker.bonus,
                                    )}`
                                  : `${enteredBonusMarker.bonus}%`}
                              </span>
                            )}
                          </div>
                          {amountViolation && (
                            <p className="text-[#ff4646] text-xs mb-2 xl:text-sm">
                              {t(
                                amountViolation.bound === 'min'
                                  ? 'amount-below-minimum'
                                  : 'amount-above-maximum',
                                {
                                  amount: numberWithCommas(
                                    amountViolation.value,
                                  ),
                                },
                              )}
                            </p>
                          )}
                          <AmountSelector
                            amount={amount}
                            setAmount={setAmount}
                            amounts={resolvePresetAmounts(
                              selectedPaymentChannel.fixed_amount,
                              selectedPaymentChannel.deposit_bonus_tiers,
                            )}
                            badges={activePromo?.linear}
                            tiers={selectedPaymentChannel.deposit_bonus_tiers}
                            showBonuses={showAmountBonuses}
                            selectedAmount={selectedAmount}
                            onSelectAmount={amount => {
                              setSelectedAmount(amount)
                              // Only a channel with amount bonuses puts the two in
                              // competition — the same channel that shows the switch.
                              // Without tiers a chip carries no bonus and is just a
                              // shortcut into the amount field, so it has nothing to
                              // replace and must leave the promotion alone.
                              if (channelHasBonusTiers) setSelectedPromo(null)
                            }}
                          />

                          {bonusSectionMounted && (
                            <div
                              className={classNames(
                                // Fade + collapse the whole section in/out so it
                                // appears and disappears smoothly (e.g. when a
                                // fetch returns no promos) instead of snapping.
                                'overflow-hidden transition-all duration-300 ease-in-out',
                                showBonusSection
                                  ? 'mt-2 max-h-[240px] opacity-100'
                                  : 'mt-0 max-h-0 opacity-0',
                              )}>
                              {/* Picks the bonus source: on shows each preset's own bonus on the
                                  chips above, off shows this list instead. Rendered only when the
                                  channel has amount bonuses — with one source there is nothing to
                                  switch to, and turning it off would hide the promotions behind
                                  nothing — and only before the player's first deposit, confirmed by
                                  a resolved vip_status; a player who has already deposited (or whose
                                  status is still loading or failed to load) keeps amount bonuses on
                                  with no way to turn them off. The track stays neutral in both states
                                  and the knob carries the accent, gold in dark and blue in light.

                                  hasDeposited/depositStatusConfirmed are the only inputs here —
                                  deliberately, not a local "just submitted" flag. Two gaps that
                                  follow from that are known and accepted rather than unhandled:
                                  1. This modal never sees a definitive payment-outcome signal (only
                                     `back`/`resize` postMessages from the iframe, no `success`/
                                     `failed`), so there is nothing trustworthy to unlock the switch
                                     on if a submission were tracked and later turned out to have
                                     failed — tracking "submitted" would have to either stay locked
                                     forever after ANY submission (including abandoned ones) or guess
                                     at an unverified signal, which is worse.
                                  2. hasDeposited itself can still be stale as of the moment it is
                                     read — the backend's own settlement of a just-made deposit is
                                     asynchronous and can outrace any client-side refetch (see
                                     refetchStatus in handleBack), including across a modal
                                     close/reopen. Closing that fully needs either the backend
                                     settling synchronously or a persisted client-side record, both
                                     out of scope here.
                                  Either way, the switch is UI convenience, not the enforcement
                                  boundary: `autopromo`/`is_bonus_tier` in onSubmit's request body are
                                  client-computed and sent regardless of what this switch shows, so
                                  the backend must independently validate deposit eligibility at
                                  submit time — no amount of client gating substitutes for that. */}
                              <div className="flex items-start gap-2">
                                <p className="deposit-section-title font-semibold uppercase text-sm text-txt inline-block xl:text-base dark:text-[#f6d56c] dark:font-bold relative">
                                  {t('bonus-available')}
                                </p>
                                {channelHasBonusTiers &&
                                  !hasDeposited &&
                                  depositStatusConfirmed && (
                                    <button
                                      type="button"
                                      role="switch"
                                      aria-checked={bonusesOn}
                                      aria-label={t('bonus-available')}
                                      onClick={() => {
                                        setBonusesOn(prev => !prev)
                                      }}
                                      className="relative mt-[2px] h-[18px] w-[32px] shrink-0 rounded-full bg-secondary-txt-20 transition-colors duration-200 xl:mt-[3px]">
                                      <span
                                        className={classNames(
                                          'absolute top-[2px] h-[14px] w-[14px] rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.35)] transition-all duration-200',
                                          bonusesOn
                                            ? 'left-[16px] bg-highlight-gold'
                                            : 'left-[2px] bg-secondary-txt',
                                        )}
                                      />
                                    </button>
                                  )}
                              </div>
                              <div
                                className={classNames(
                                  'promo-list-container flex gap-3 overflow-x-auto py-2',
                                  // Fade the real bonuses in as the skeleton hides
                                  // (0.2s opacity) so the swap is smooth.
                                  !showBonusSkeleton && 'animate-fadeIn',
                                  // Bonuses off: the list stays on screen so a
                                  // player can see what is on offer, but greyed
                                  // and inert. `grayscale` flattens the promo
                                  // tiles' accent fills, which a colour swap
                                  // would not.
                                  promosDimmed &&
                                    'grayscale opacity-50 pointer-events-none',
                                )}>
                                {showBonusSkeleton
                                  ? Array.from(Array(3).keys()).map(i => (
                                      <div
                                        key={i}
                                        className="animate-pulse duration-[200] flex-1 min-w-0 h-[55px] flex items-center relative px-2 py-2 rounded-lg bg-tertiary xl:h-[70px] xl:px-3 xl:py-4"></div>
                                    ))
                                  : promoList.map(promo => (
                                      <button
                                        className={classNames(
                                          activePromo?.id === promo.id
                                            ? 'deposit-bonus-active bg-highlight shadow-[0px_0px_5px_2px_rgba(0,_166,_255,_0.5)] dark:!bg-transparent dark:!border-[#f5c542] dark:!shadow-[0_0_16px_rgba(245,197,66,0.14)]'
                                            : 'bg-tertiary dark:bg-[#252525]',
                                          promoTileWidth,
                                          'h-[55px] flex items-center relative px-2 py-2 rounded-lg shrink-0 xl:h-[70px] xl:px-3 xl:py-4 dark:rounded-lg dark:border dark:border-solid dark:border-[rgba(255,255,255,0.08)] dark:transition-all dark:duration-200',
                                        )}
                                        key={promo.id}
                                        // Greying alone left these focusable
                                        // and clickable by keyboard while
                                        // bonuses were off.
                                        disabled={promosDimmed}
                                        onClick={() => {
                                          if (
                                            promo.disabled &&
                                            promo.disabled_reason
                                          ) {
                                            return toastMessage.show({
                                              text: promo.disabled_reason,
                                              isValid: false,
                                            })
                                          }
                                          // Choosing a promotion is choosing it over the amount
                                          // bonus, so the chip's selection goes — its value stays
                                          // in the field. Only where an amount bonus exists to be
                                          // chosen over, though: on a tierless channel the chip is
                                          // the player's amount, not a rival bonus, and clearing it
                                          // just unhighlighted what they had picked.
                                          if (channelHasBonusTiers)
                                            setSelectedAmount(null)
                                          setSelectedPromo(
                                            activePromo?.id === promo.id
                                              ? null
                                              : promo,
                                          )
                                        }}>
                                        <p
                                          className={classNames(
                                            activePromo?.id === promo.id
                                              ? 'text-white'
                                              : 'text-txt',
                                            'promo-name text-xs font-bold w-full overflow-hidden text-ellipsis text-left xl:text-sm',
                                          )}>
                                          {promo.label}
                                        </p>
                                        <img
                                          className={classNames(
                                            activePromo?.id === promo.id
                                              ? 'opacity-100'
                                              : 'opacity-60',
                                            'w-6 h-6 ml-2 xl:w-8 xl:h-8 xl:ml-3',
                                          )}
                                          src={gift}
                                          alt="gift"
                                        />
                                      </button>
                                    ))}
                              </div>
                            </div>
                          )}

                          <div className="flx-btw-ctr mt-2">
                            <div className="flx-btw-ctr w-full">
                              <label className="mr-3">
                                <span className="text-sm text-secondary-txt xl:text-base">
                                  {t('bonus')}:{' '}
                                </span>
                                <span className="text-base text-highlight font-semibold xl:text-lg dark:text-[#f5c542]">
                                  {bonusValue
                                    ? `+$${numberWithCommas(bonusValue)}`
                                    : '$0'}
                                </span>
                              </label>
                              <p className="text-right">
                                <span className="text-sm text-secondary-txt xl:text-base">
                                  {t('total-credit')}:{' '}
                                </span>
                                <span className="text-base text-highlight font-semibold xl:text-lg dark:text-[#f5c542]">
                                  ${numberWithCommas(totalToReceive)}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="px-3 xl:px-6 pb-6 xl:pb-12">
                        <div className="mt-3 xl:mt-4">
                          {isLoading ? (
                            <Spinner />
                          ) : (
                            <div className="game-card-glow glow-gold inline-flex w-full rounded-[10px]">
                              <GlowRing className="[--glow-ring-width:2px]" />
                              <GlowButton
                                className="w-full h-[50px] rounded-xl text-base xl:h-[56px] xl:text-lg dark:rounded-lg"
                                disabled={
                                  !Number(amountValue) ||
                                  !!amountViolation ||
                                  // showAmountBonuses/bonusesOn don't wait on
                                  // depositStatusConfirmed, so a refetch in
                                  // flight (e.g. handleBack's, or the initial
                                  // mount) hides the switch while the amount
                                  // bonus is still silently in effect. Block
                                  // submission until status is confirmed, so a
                                  // rapid retry can't ride an opt-out-then-back
                                  // bonus reset past the player unnoticed.
                                  (channelHasBonusTiers &&
                                    !depositStatusConfirmed)
                                }
                                onClick={() => {
                                  let tab = null
                                  if (
                                    selectedPaymentChannel.is_external_link ===
                                      1 ||
                                    selectedPaymentChannel.merchant ===
                                      'conekta' ||
                                    [377].includes(+selectedPaymentChannel.id)
                                  ) {
                                    tab = window.open()
                                    if (tab) {
                                      tab.document.body.innerHTML = LOADING_HTML
                                    }
                                  }
                                  onSubmit(tab)
                                }}>
                                {t('deposit')}
                              </GlowButton>
                            </div>
                          )}
                          <p className="text-[10px] xl:text-xs text-secondary-txt text-center mt-3">
                            🛡 Pagos procesados en México • Canales locales
                            autorizados
                          </p>
                          {hasCryptoChannel && (
                            <p className="flex items-center justify-center gap-1 text-[10px] xl:text-xs text-secondary-txt text-center mt-1">
                              <Icon
                                id="outline-exclamation-circle"
                                width={12}
                                height={12}
                                className="shrink-0"
                                fill="var(--secondary-txt)"
                              />
                              {t('no-bonus-on-crypto')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="rounded-xl overflow-hidden">
                <iframe
                  className="pointer-events-none absolute w-0 h-full  border-[none] ring-0"
                  ref={iframeRef}
                  // Follows the height the payment page reports, so the frame ends
                  // where its content ends instead of running to the bottom of the
                  // viewport. Unset while unknown, leaving handleIframeStyle's
                  // class height; the class max-h- still caps a tall page.
                  style={frameHeight ? { height: frameHeight } : undefined}
                  onLoad={() => {
                    setIsLoading(false)
                  }}
                  allow="clipboard-read self *; clipboard-write self *"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Portal>
  )
}

export default NiceModal.create(DepositModal)
